import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  NoSymbolIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import ipaddr from 'ipaddr.js';
import {
  fetchKnownMaliciousIPs,
  addKnownMaliciousIP,
  updateKnownMaliciousIP,
  removeKnownMaliciousIP,
} from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Badge from './ui/Badge';
import { ipMaliciousTypeTone } from './ui/badgeTones';
import Pagination from './Pagination';
import IpActivitySearchModal from './IpActivitySearchModal';

const SUGGESTED_TYPES = ['c2', 'phishing', 'scanner', 'tor_exit', 'known_proxy', 'malware_drop', 'exfil', 'unknown'];

function formatDate(dateStr) {
  if (!dateStr) return '\u2014';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy');
  } catch {
    return dateStr;
  }
}

// Returns { ok: true, canonical } or { ok: false, error }
function tryCanonicalize(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return { ok: false, error: 'IP address is required.' };
  try {
    const parsed = ipaddr.process(trimmed);
    return { ok: true, canonical: parsed.toString().toLowerCase() };
  } catch {
    return { ok: false, error: `'${trimmed}' is not a valid IP address.` };
  }
}

export default function KnownMaliciousIPsManager() {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;
  const upn = user?.userDetails || '';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterQuery, setFilterQuery] = useState('');
  const [filterType, setFilterType] = useState('all');

  // Add form state
  const [addIp, setAddIp] = useState('');
  const [addType, setAddType] = useState('c2');
  const [addTypeOther, setAddTypeOther] = useState('');
  const [addSource, setAddSource] = useState('');
  const [addNotes, setAddNotes] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  // Pre-fill source default once user identity is available
  useEffect(() => {
    if (!addSource && upn) setAddSource(`analyst:${upn}`);
  }, [upn]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Delete confirm state
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Inline edit state (type / source / notes; IP is immutable)
  const [editingId, setEditingId] = useState(null);
  const [editType, setEditType] = useState('c2');
  const [editTypeOther, setEditTypeOther] = useState('');
  const [editSource, setEditSource] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  // Cross-tenant activity search modal
  const [searchModalRow, setSearchModalRow] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Resizable columns: IP, Type, Source, Notes, Added, Actions
  const [colWidths, setColWidths] = useState([260, 130, 220, 320, 130, 130]);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchKnownMaliciousIPs();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load known malicious IPs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let out = rows;
    if (filterType !== 'all') {
      out = out.filter((r) => String(r.type || '').toLowerCase() === filterType);
    }
    const q = filterQuery.trim().toLowerCase();
    if (q) {
      out = out.filter(
        (r) =>
          (r.ipAddress && r.ipAddress.toLowerCase().includes(q)) ||
          (r.source && r.source.toLowerCase().includes(q)) ||
          (r.notes && r.notes.toLowerCase().includes(q))
      );
    }
    return out;
  }, [rows, filterQuery, filterType]);

  useEffect(() => { setCurrentPage(1); }, [filterQuery, filterType]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIdx = (safeCurrentPage - 1) * pageSize;
  const paged = filtered.slice(startIdx, startIdx + pageSize);

  // Type values seen in data plus suggested set, used to populate filter dropdown
  const knownTypes = useMemo(() => {
    const set = new Set(SUGGESTED_TYPES);
    rows.forEach((r) => { if (r.type) set.add(String(r.type).toLowerCase()); });
    return Array.from(set);
  }, [rows]);

  const resolveTypeValue = (selected, otherValue) => {
    if (selected === 'other') return String(otherValue || '').trim().toLowerCase();
    return selected;
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddError(null);

    const canon = tryCanonicalize(addIp);
    if (!canon.ok) { setAddError(canon.error); return; }

    const typeVal = resolveTypeValue(addType, addTypeOther);
    if (!typeVal) { setAddError('Type is required.'); return; }

    const sourceVal = addSource.trim();
    if (!sourceVal) { setAddError('Source is required.'); return; }

    setAdding(true);
    try {
      const created = await addKnownMaliciousIP({
        ipAddress: canon.canonical,
        type:      typeVal,
        source:    sourceVal,
        notes:     addNotes.trim(),
      });
      // Upsert semantics: replace existing row if present, else prepend
      setRows((prev) => {
        const without = prev.filter((r) => r.ipAddress !== created.ipAddress);
        return [created, ...without];
      });
      setAddIp('');
      setAddNotes('');
      // Keep type/source defaults so analyst can rapid-add a batch
    } catch (err) {
      setAddError(err.message || 'Failed to add IP');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (ip) => {
    setRemoving(true);
    try {
      await removeKnownMaliciousIP(ip);
      setRows((prev) => prev.filter((r) => r.ipAddress !== ip));
      setConfirmRemove(null);
    } catch (err) {
      setError(err.message || 'Failed to remove IP');
    } finally {
      setRemoving(false);
    }
  };

  const startEdit = (row) => {
    setEditError(null);
    setConfirmRemove(null);
    setEditingId(row.ipAddress);
    const t = String(row.type || '').toLowerCase();
    if (SUGGESTED_TYPES.includes(t)) {
      setEditType(t);
      setEditTypeOther('');
    } else {
      setEditType('other');
      setEditTypeOther(t);
    }
    setEditSource(row.source || '');
    setEditNotes(row.notes || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTypeOther('');
    setEditSource('');
    setEditNotes('');
    setEditError(null);
  };

  const handleSaveEdit = async (row) => {
    setEditError(null);
    const typeVal = resolveTypeValue(editType, editTypeOther);
    if (!typeVal) { setEditError('Type is required.'); return; }
    if (!editSource.trim()) { setEditError('Source is required.'); return; }

    setSavingEdit(true);
    try {
      const updated = await updateKnownMaliciousIP(row.ipAddress, {
        type:   typeVal,
        source: editSource.trim(),
        notes:  editNotes.trim(),
      });
      setRows((prev) => prev.map((item) => (item.ipAddress === row.ipAddress ? { ...item, ...updated } : item)));
      cancelEdit();
    } catch (err) {
      setEditError(err.message || 'Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <NoSymbolIcon className="h-7 w-7 text-red-500" />
          <h1 className="text-2xl font-bold text-gray-100">Known Malicious IPs (global)</h1>
        </div>
        <p className="text-sm text-gray-500 max-w-3xl">
          Cross-tenant threat-intel list. When an analyst confirms an IP is malicious during one tenant's investigation,
          adding it here causes a detection rule to fire on matches across every monitored tenant. Single partition; deduped by canonical IP.
        </p>
      </div>

      {/* Add Form (admin only) */}
      {isAdmin ? (
        <div className="card mb-6">
          <h2 className="text-sm font-semibold text-gray-300 mb-4">Add Malicious IP</h2>
          <form onSubmit={handleAdd} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">IP Address</label>
                <input
                  type="text"
                  placeholder="203.0.113.42 or 2001:db8::1"
                  value={addIp}
                  onChange={(e) => setAddIp(e.target.value)}
                  className="input-field font-mono text-xs"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Type</label>
                <div className="flex gap-2">
                  <select
                    value={addType}
                    onChange={(e) => setAddType(e.target.value)}
                    className="input-field"
                  >
                    {SUGGESTED_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    <option value="other">Other...</option>
                  </select>
                  {addType === 'other' && (
                    <input
                      type="text"
                      placeholder="custom type"
                      value={addTypeOther}
                      onChange={(e) => setAddTypeOther(e.target.value)}
                      className="input-field font-mono text-xs"
                      required
                    />
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Source</label>
                <input
                  type="text"
                  placeholder={`analyst:${upn || 'me'}`}
                  value={addSource}
                  onChange={(e) => setAddSource(e.target.value)}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Notes (optional)</label>
                <input
                  type="text"
                  placeholder="Incident summary, malware family, alert ID..."
                  value={addNotes}
                  onChange={(e) => setAddNotes(e.target.value)}
                  className="input-field"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={adding || !addIp.trim()}
                className="btn-primary flex items-center gap-2"
              >
                {adding ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlusIcon className="h-4 w-4" />}
                Add / Update
              </button>
              <span className="text-xs text-gray-600">Re-adding an existing IP overwrites type/source/notes; the original first-seen date is preserved.</span>
            </div>
          </form>
          {addError && <p className="mt-2 text-sm text-red-400">{addError}</p>}
        </div>
      ) : (
        <div className="card mb-6 flex items-center gap-3 text-sm text-gray-400">
          <LockClosedIcon className="h-4 w-4 text-gray-500" />
          <span>Read-only view — admin access is required to add, edit, or remove entries.</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={load} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}

      {/* Edit error banner */}
      {editError && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{editError}</span>
        </div>
      )}

      {/* Filter and Count */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Filter by IP, source, or notes..."
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              className="input-field pl-9"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="input-field max-w-[180px]"
          >
            <option value="all">All types</option>
            {knownTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <span className="text-xs text-gray-500">
          {filtered.length} {filtered.length === 1 ? 'entry' : 'entries'}
        </span>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            ref={tableRef}
            className="text-sm"
            style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}
          >
            <thead>
              <tr className="border-b border-gray-800">
                {['IP Address', 'Type', 'Source', 'Notes', 'Added', 'Actions'].map((label, colIdx) => (
                  <th
                    key={label}
                    ref={(el) => { thRefs.current[colIdx] = el; }}
                    className={`${colIdx === 5 ? 'text-center' : 'text-left'} text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap relative select-none`}
                    style={{ width: `${colWidths[colIdx]}px` }}
                  >
                    <span className="truncate">{label}</span>
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => startResize(e, colIdx)}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span className="text-gray-500 text-sm">Loading known malicious IPs...</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <NoSymbolIcon className="h-10 w-10 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-400 font-medium">No entries found</p>
                    <p className="text-gray-600 text-sm mt-1">
                      {filterQuery || filterType !== 'all'
                        ? 'Try adjusting your filters.'
                        : isAdmin ? 'Add an IP above to get started.' : 'No entries yet.'}
                    </p>
                  </td>
                </tr>
              )}

              {!loading && paged.map((r) => {
                const isEditing = editingId === r.ipAddress;
                return (
                <tr key={r.ipAddress} className="hover:bg-gray-800/50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-gray-200 truncate overflow-hidden" title={r.ipAddress}>
                    {r.ipAddress}
                  </td>
                  <td className="px-4 py-3 truncate overflow-hidden">
                    {isEditing ? (
                      <div className="flex flex-col gap-1">
                        <select
                          value={editType}
                          onChange={(e) => setEditType(e.target.value)}
                          className="input-field text-xs py-1"
                        >
                          {SUGGESTED_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                          <option value="other">Other...</option>
                        </select>
                        {editType === 'other' && (
                          <input
                            type="text"
                            value={editTypeOther}
                            onChange={(e) => setEditTypeOther(e.target.value)}
                            className="input-field text-xs py-1 font-mono"
                            placeholder="custom"
                          />
                        )}
                      </div>
                    ) : r.type ? (
                      <Badge tone={ipMaliciousTypeTone(r.type)} dot>{r.type}</Badge>
                    ) : (
                      <span className="text-gray-700">{'\u2014'}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs truncate overflow-hidden" title={r.source || ''}>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editSource}
                        onChange={(e) => setEditSource(e.target.value)}
                        className="input-field w-full text-xs py-1"
                      />
                    ) : (
                      r.source || '\u2014'
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 truncate overflow-hidden" title={r.notes || ''}>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        className="input-field w-full text-xs py-1"
                      />
                    ) : (
                      r.notes || '\u2014'
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs truncate overflow-hidden">
                    {formatDate(r.addedUtc)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {!isAdmin ? (
                      <span className="text-gray-700">{'\u2014'}</span>
                    ) : isEditing ? (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleSaveEdit(r)}
                          disabled={savingEdit}
                          className="text-gray-600 hover:text-green-400 transition-colors p-1"
                          title="Save"
                        >
                          {savingEdit ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={savingEdit}
                          className="text-gray-600 hover:text-gray-300 transition-colors p-1"
                          title="Cancel"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ) : confirmRemove === r.ipAddress ? (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleRemove(r.ipAddress)}
                          disabled={removing}
                          className="btn-danger px-2 py-1 text-xs"
                        >
                          {removing ? 'Removing...' : 'Confirm'}
                        </button>
                        <button
                          onClick={() => setConfirmRemove(null)}
                          className="btn-secondary px-2 py-1 text-xs"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => setSearchModalRow(r)}
                          className="text-gray-600 hover:text-amber-400 transition-colors p-1"
                          title="Search cross-tenant activity for this IP"
                        >
                          <MagnifyingGlassIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => startEdit(r)}
                          className="text-gray-600 hover:text-blue-400 transition-colors p-1"
                          title="Edit type / source / notes"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setConfirmRemove(r.ipAddress)}
                          className="text-gray-600 hover:text-red-400 transition-colors p-1"
                          title="Remove IP"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length > 0 && (
          <Pagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        )}
      </div>

      <IpActivitySearchModal
        isOpen={!!searchModalRow}
        ipRow={searchModalRow}
        onClose={() => setSearchModalRow(null)}
      />
    </div>
  );
}
